import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
  FeatureFlagConfig, 
  FeatureFlagContextType, 
  FeatureFlag,
  getCurrentEnvironment 
} from './types';
import { getEnvironmentFlags } from './config';
import { useAuthStore } from '../stores/authStore';

// Web-compatible AsyncStorage implementation
const AsyncStorage = {
  getItem: async (key: string) => {
    try {
      if (typeof localStorage !== 'undefined' && localStorage) {
        return localStorage.getItem(key);
      }
      if (typeof sessionStorage !== 'undefined' && sessionStorage) {
        return sessionStorage.getItem(key);
      }
      console.warn('No storage available in this browser');
      return null;
    } catch (error) {
      console.warn('Storage access failed:', error);
      return null;
    }
  },
  setItem: async (key: string, value: string) => {
    try {
      if (typeof localStorage !== 'undefined' && localStorage) {
        localStorage.setItem(key, value);
        return;
      }
      if (typeof sessionStorage !== 'undefined' && sessionStorage) {
        sessionStorage.setItem(key, value);
        return;
      }
      console.warn('No storage available in this browser');
    } catch (error) {
      console.warn('Storage write failed:', error);
    }
  },
  removeItem: async (key: string) => {
    try {
      if (typeof localStorage !== 'undefined' && localStorage) {
        localStorage.removeItem(key);
        return;
      }
      if (typeof sessionStorage !== 'undefined' && sessionStorage) {
        sessionStorage.removeItem(key);
        return;
      }
    } catch (error) {
      console.warn('Storage removal failed:', error);
    }
  },
};

// Create Feature Flag Context
const FeatureFlagContext = createContext<FeatureFlagContextType | undefined>(undefined);

// Storage keys
const STORAGE_KEYS = {
  FEATURE_FLAGS: '@stsc_feature_flags',
  ENVIRONMENT: '@stsc_environment',
};

interface FeatureFlagProviderProps {
  children: ReactNode;
  initialEnvironment?: 'development' | 'staging' | 'production';
}

export const FeatureFlagProvider: React.FC<FeatureFlagProviderProps> = ({ 
  children, 
  initialEnvironment 
}) => {
  const [flags, setFlags] = useState<FeatureFlagConfig>({});
  const [environment, setEnvironment] = useState<'development' | 'staging' | 'production'>(
    initialEnvironment || getCurrentEnvironment()
  );
  const [isLoading, setIsLoading] = useState(true);
  
  const user = useAuthStore((state) => state.user);

  // Initialize feature flags
  useEffect(() => {
    initializeFlags();
  }, [environment]);

  const initializeFlags = async () => {
    try {
      setIsLoading(true);
      
      // Try to load cached flags from storage
      const cachedFlags = await AsyncStorage.getItem(STORAGE_KEYS.FEATURE_FLAGS);
      const cachedEnvironment = await AsyncStorage.getItem(STORAGE_KEYS.ENVIRONMENT);
      
      // Use cached flags if environment matches, otherwise use default
      if (cachedFlags && cachedEnvironment === environment) {
        const parsedFlags = JSON.parse(cachedFlags);
        setFlags(parsedFlags);
      } else {
        // Load environment-specific flags
        const environmentFlags = getEnvironmentFlags(environment);
        setFlags(environmentFlags);
        
        // Cache the flags
        await AsyncStorage.setItem(STORAGE_KEYS.FEATURE_FLAGS, JSON.stringify(environmentFlags));
        await AsyncStorage.setItem(STORAGE_KEYS.ENVIRONMENT, environment);
      }
    } catch (error) {
      console.error('Error initializing feature flags:', error);
      
      // Fallback to environment flags
      const environmentFlags = getEnvironmentFlags(environment);
      setFlags(environmentFlags);
    } finally {
      setIsLoading(false);
    }
  };

  // Check if a feature is enabled for a specific user
  const isFeatureEnabled = (
    featureKey: string, 
    userId?: string, 
    userType?: string
  ): boolean => {
    const flag = flags[featureKey];
    
    if (!flag) {
      console.warn(`Feature flag '${featureKey}' not found`);
      return false;
    }

    // Check if feature is globally disabled
    if (!flag.enabled) {
      return false;
    }

    // Use current user if not provided
    const currentUserId = userId || user?.id;
    const currentUserType = userType || user?.role;

    // Check user targeting if configured
    if (flag.userTargeting && currentUserType) {
      const { userTypes, userIds, rolloutPercentage } = flag.userTargeting;

      // Check user type targeting
      if (userTypes && !userTypes.includes(currentUserType as any)) {
        return false;
      }

      // Check specific user ID targeting
      if (userIds && currentUserId && !userIds.includes(currentUserId)) {
        return false;
      }

      // Check rollout percentage for targeted users
      if (rolloutPercentage < 100 && currentUserId) {
        const userHash = hashString(currentUserId + featureKey) % 100;
        if (userHash >= rolloutPercentage) {
          return false;
        }
      }
    } else if (flag.rolloutPercentage < 100 && currentUserId) {
      // Check global rollout percentage
      const userHash = hashString(currentUserId + featureKey) % 100;
      if (userHash >= flag.rolloutPercentage) {
        return false;
      }
    }

    // Check feature dependencies
    if (flag.dependencies) {
      for (const dependency of flag.dependencies) {
        if (!isFeatureEnabled(dependency, userId, userType)) {
          return false;
        }
      }
    }

    return true;
  };

  // Get a specific feature flag
  const getFeatureFlag = (featureKey: string): FeatureFlag | undefined => {
    return flags[featureKey];
  };

  // Update a feature flag
  const updateFeatureFlag = async (featureKey: string, updates: Partial<FeatureFlag>) => {
    try {
      const currentFlag = flags[featureKey];
      if (!currentFlag) {
        console.warn(`Feature flag '${featureKey}' not found`);
        return;
      }

      const updatedFlag = {
        ...currentFlag,
        ...updates,
        updatedAt: new Date().toISOString(),
      };

      const updatedFlags = {
        ...flags,
        [featureKey]: updatedFlag,
      };

      setFlags(updatedFlags);

      // Cache updated flags
      await AsyncStorage.setItem(STORAGE_KEYS.FEATURE_FLAGS, JSON.stringify(updatedFlags));

      console.log(`Feature flag '${featureKey}' updated:`, updates);
    } catch (error) {
      console.error('Error updating feature flag:', error);
    }
  };

  // Refresh flags from remote source (for future implementation)
  const refreshFlags = async () => {
    try {
      // In the future, this could fetch from a remote API
      // For now, just reload environment flags
      const environmentFlags = getEnvironmentFlags(environment);
      setFlags(environmentFlags);
      
      await AsyncStorage.setItem(STORAGE_KEYS.FEATURE_FLAGS, JSON.stringify(environmentFlags));
      console.log('Feature flags refreshed');
    } catch (error) {
      console.error('Error refreshing feature flags:', error);
    }
  };

  const contextValue: FeatureFlagContextType = {
    flags,
    isFeatureEnabled,
    getFeatureFlag,
    updateFeatureFlag,
    refreshFlags,
    environment,
  };

  if (isLoading) {
    // Return loading state or the children with empty flags
    return (
      <FeatureFlagContext.Provider value={contextValue}>
        {children}
      </FeatureFlagContext.Provider>
    );
  }

  return (
    <FeatureFlagContext.Provider value={contextValue}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

// Custom hook to use feature flags
export const useFeatureFlags = (): FeatureFlagContextType => {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagProvider');
  }
  return context;
};

// Custom hook to check if a specific feature is enabled
export const useFeatureEnabled = (featureKey: string): boolean => {
  const { isFeatureEnabled } = useFeatureFlags();
  const user = useAuthStore((state) => state.user);
  
  return isFeatureEnabled(featureKey, user?.id, user?.role);
};

// Simple hash function for consistent user bucketing
const hashString = (str: string): number => {
  let hash = 0;
  if (str.length === 0) return hash;
  
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  return Math.abs(hash);
};
